/**
 * Represents a Textfield wrapper that can be fitted to given bounds.
 */
import { Serializer } from "../utils/Serializer";

export class Text extends PIXI.Container {
    private _config: TextConfig;
    protected _text: PIXI.Text;
    protected _originalFontSize: number;
    protected _rect: PIXI.Graphics;


    constructor(config: any, text?: string, style?: any) {
        super();

        this._config = new TextConfig();
        this._config.serialize(config);

        this._text = new PIXI.Text(text, style);

        this._originalFontSize = this._text.style.fontSize as number;

        this.drawDebugRect();

        this.addChild(this._text);

        this.fitToBounds();
        this.positionText();
    }

    get text(): string {
        return this._text.text;
    }

    /**
     * Sets the text of the textfield. Repositions and fits to bounds (if given)
     * @param {string} value
     */
    set text(value: string) {
        if (value != this._text.text) {
            this._text.text = value;

            this.fitToBounds();
            this.positionText();
        }
    }

    get config(): any {
        return this._config
    }

    /**
     * Serializes config and recalculates text.
     * @param conf Object with config params
     */
    set config(conf: any) {
        this._config.serialize(conf);

        this.drawDebugRect();
        this.fitToBounds();
        this.positionText();
    }

    get style(): PIXI.TextStyle {
        return this._text.style;
    }

    set style(value: PIXI.TextStyle) {
        if (value.fontSize == undefined)
            value.fontSize = this._originalFontSize;

        this._text.style = value;

        this._originalFontSize = this._text.style.fontSize as number;

        this.fitToBounds();
        this.positionText();
    }

    //sample comment check
    set align(value: string) {
        this._config.align = value;

        this.fitToBounds();
        this.positionText();
    }


    dispose() {
        this._text.destroy();
        if (this._rect) {
            this._rect.destroy();
        }

        this.destroy();
    }

    /**
     * Fits text depending on given bounds and font
     *
     */
    protected fitToBounds() {
        if (!this._config.shouldScale)
            return;

        this._text.style.fontSize = this._originalFontSize;
        this._text.style = this._text.style;

        var fontSize: number = this._text.style.fontSize as number;
        while (this._text.width > this._config.bounds.width &&
        fontSize > this._config.minFontSize) {
            fontSize--;
            this._text.style.fontSize = fontSize;

            this._text.style = this._text.style;
        }
    }

    /**
     * Positions text depending on style
     */
    protected positionText() {
        const fontProps: PIXI.TextMetrics = PIXI.TextMetrics.measureText(this._text.text, this._text.style);

        //sample comment check
        if (this._config.align == "left")
            this._text.x = 0;
        else if (this._config.align == "center")
            this._text.x = Math.round((this._config.bounds.width - this._text.width) / 2);
        else if (this._config.align == "right")
            this._text.x = Math.round(this._config.bounds.width - this._text.width);


        if (this._config.verticalAlign === "middle") {
            this._text.y = Math.round((this._config.bounds.height - this._text.height) / 2);
        } else if (this._config.verticalAlign === "top") {
            this._text.y = -fontProps.fontProperties.descent;
        } else if (this._config.verticalAlign === "bottom") {
            this._text.y = Math.round(this._config.bounds.height - fontProps.fontProperties.ascent);
        }
    }

    /**
     * Draws semi-transparent rectangle with the width of the container of the text
     */
    protected drawDebugRect() {
        if (this._rect) this._rect.destroy();

        if (this._config.debug) {
            this._rect = new PIXI.Graphics();
            this._rect.beginFill(0xff0000, 0.3);
            this._rect.drawRect(0, 0, this._config.bounds.width, this._config.bounds.height);
            this._rect.endFill();

            this.addChild(this._rect);
        }
    }
}


export class TextConfig extends Serializer {
    /**
     * Used when calculating text. Final text will not expand beyond the bounds
     * @type {{width: number; height: number}}
     */
    bounds: any = {width: 100, height: 50};
    align: string = "left";
    verticalAlign: string = "middle";
    /**
     * Same as shouldFitToBounds. True if bounds should be respected
     * @type {boolean}
     */
    shouldScale: boolean = false; // TODO: rename this to shouldFitToBounds
    minFontSize: number = 4;
    /**
     * Draws debug rectangle that shows text's container bounds.
     * @type {boolean}
     */
    debug: boolean = false;
}
